import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import AskTeamAssist from 'actions/team_assist/ask_team_assist';
import ClearTeamAssist from 'actions/team_assist/clear_team_assist';
import ToggleTeamAssistPanel from 'actions/team_assist/toggle_team_assist_panel';
import connect from 'components/lib/connect';
import TeamAssistIcon from 'components/lib/icons/team_assist_icon';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import MessageBubble, { AssistantRow } from 'components/customer/team_assist/team_assist_message';
import TeamAssistEmptyStateIcon from 'components/customer/team_assist/team_assist_empty_state_icon';
import TeamAssistModal from 'components/customer/team_assist/team_assist_modal';
import { TeamAssistInput } from 'components/customer/team_assist/team_assist_panel_shared';

function TeamAssistPanel({ customerId, isLoading, messages }) {
  const executeAction = useExecuteAction();
  const [inputValue, setInputValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const prevLoadingRef = useRef(isLoading);
  const hasScrolledSinceMountRef = useRef(false);

  const messagesRef = useRef(messages);
  const lastMessageId = messages.length ? messages[messages.length - 1].id : null;

  useEffect(() => {
    if (!messagesEndRef.current) {
      return;
    }

    messagesRef.current = messages;
    const justFinishedLoading = prevLoadingRef.current && !isLoading;
    prevLoadingRef.current = isLoading;
    const isInitialOrReturning = !hasScrolledSinceMountRef.current;
    if (isInitialOrReturning) {
      hasScrolledSinceMountRef.current = true;
    }
    const useInstantScroll = justFinishedLoading || isInitialOrReturning;
    messagesEndRef.current.scrollIntoView({
      behavior: useInstantScroll ? 'auto' : 'smooth',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on lastMessageId so we don't re-run when messages is a new array reference
  }, [isLoading, lastMessageId]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isModalOpen]);

  const handleSubmit = useCallback(() => {
    const question = inputValue.trim();
    if (!question || isLoading) {
      return;
    }

    setInputValue('');
    executeAction(AskTeamAssist, { customerId, question });
  }, [customerId, executeAction, inputValue, isLoading]);

  const handleClear = useCallback(() => {
    if (!messages.length) {
      return;
    }
    executeAction(ClearTeamAssist, { customerId });
  }, [customerId, executeAction, messages.length]);

  const onKeyDown = useCallback(
    evt => {
      if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        handleSubmit();
      } else if (evt.key === 'Escape') {
        executeAction(ToggleTeamAssistPanel);
      }
    },
    [executeAction, handleSubmit]
  );

  const onModalKeyDown = useCallback(
    evt => {
      if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        handleSubmit();
      } else if (evt.key === 'Escape') {
        evt.preventDefault();
        setIsModalOpen(false);
      }
    },
    [handleSubmit]
  );

  const onInputChange = useCallback(evt => {
    setInputValue(evt.target.value);
  }, []);

  return (
    <>
      <StyledPanel data-aid="teamAssistPanel" tabIndex="0">
        <Header>
          <StyledHeaderIcon height="16" width="16" />
          <HeaderText>Team Assist</HeaderText>
          <ExpandButton
            data-aid="teamAssistExpandButton"
            onClick={() => setIsModalOpen(true)}
            title="Expand"
            type="button"
          >
            <i className="fa fa-expand" />
          </ExpandButton>
          <ClearButton
            data-aid="teamAssistClearButton"
            disabled={!messages.length}
            onClick={handleClear}
            title="Clear conversation"
            type="button"
          >
            <i className="fa fa-trash" />
          </ClearButton>
        </Header>
        <MessagesArea>
          {messages.length === 0 && !isLoading && (
            <EmptyState>
              <EmptyStateIconWrapper>
                <TeamAssistEmptyStateIcon size={100} />
              </EmptyStateIconWrapper>
              <EmptyStateText>Ask anything about this customer—answers, history, or next steps.</EmptyStateText>
            </EmptyState>
          )}
          {messages.map(msg => (
            <MessageBubble key={msg.id} msg={msg} />
          ))}
          {isLoading && (
            <AssistantRow>
              <LoadingDots>
                <Dot style={{ animationDelay: '0s' }} />
                <Dot style={{ animationDelay: '0.2s' }} />
                <Dot style={{ animationDelay: '0.4s' }} />
              </LoadingDots>
            </AssistantRow>
          )}
          <div ref={messagesEndRef} />
        </MessagesArea>
        <InputArea>
          <TeamAssistInput
            dataAid="teamAssistInput"
            dataAidSend="teamAssistSendButton"
            disabled={!inputValue.trim() || isLoading}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            onSubmit={handleSubmit}
            value={inputValue}
          />
        </InputArea>
      </StyledPanel>
      {isModalOpen && (
        <TeamAssistModal
          inputValue={inputValue}
          isLoading={isLoading}
          messages={messages}
          messagesEndRef={messagesEndRef}
          onClear={handleClear}
          onClose={() => setIsModalOpen(false)}
          onInputChange={onInputChange}
          onKeyDown={onModalKeyDown}
          onSubmit={handleSubmit}
          onUse={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}

TeamAssistPanel.propTypes = {
  customerId: PropTypes.string,
  isLoading: PropTypes.bool,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      id: PropTypes.string,
      role: PropTypes.string,
      type: PropTypes.string,
    })
  ),
};

TeamAssistPanel.defaultProps = {
  isLoading: false,
  messages: [],
};

function mapStateToProps({ getProvider }) {
  const customerId = getProvider('currentLocation').get().customerId;
  const teamAssistsProvider = getProvider('teamAssists');
  const messages = teamAssistsProvider.findAll() || [];
  const isLoading = teamAssistsProvider.isLoading();

  return { customerId, isLoading, messages };
}

export default connect(mapStateToProps)(TeamAssistPanel);

const StyledPanel = styled.div`
  box-shadow: ${p => p.theme.boxShadow.small};
  display: flex;
  flex: 0 0 25%;
  flex-direction: column;
  height: 100%;
  max-width: 360px;
  min-width: 200px;
  outline: none;
  width: 25%;
  z-index: 3;
`;

const Header = styled.div`
  align-items: center;
  border-bottom: 1px solid ${p => p.theme.colors.gray300};
  display: flex;
  gap: 8px;
  padding: 16px;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: ${p => (p.disabled ? p.theme.colors.gray300 : p.theme.colors.gray500)};
  cursor: ${p => (p.disabled ? 'default' : 'pointer')};
  font-size: 14px;
  margin-left: auto;
  padding: 4px;
  &:hover {
    color: ${p => (p.disabled ? p.theme.colors.gray300 : p.theme.colors.red400)};
  }
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  &:hover {
    color: ${p => p.theme.colors.purple400};
  }
`;

const StyledHeaderIcon = styled(TeamAssistIcon)`
  color: ${p => p.theme.colors.purple400};
  flex-shrink: 0;
`;

const HeaderText = styled.span`
  color: ${p => p.theme.colors.purple400};
  font-size: 16px;
  font-weight: ${p => p.theme.fontWeight.medium};
`;

const MessagesArea = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior-y: none;
  padding: 16px;
`;

const EmptyState = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  padding: 24px 16px;
  text-align: center;
`;

const EmptyStateIconWrapper = styled.div`
  color: ${p => p.theme.colors.purple400};
  margin-bottom: 16px;
  opacity: 0.85;
`;

const EmptyStateText = styled.p`
  color: ${p => p.theme.colors.gray600};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  margin: 0;
  max-width: 260px;
`;

const InputArea = styled.div`
  border-top: 1px solid ${p => p.theme.colors.gray300};
  padding: 12px 16px;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 12px;
`;

const Dot = styled.div`
  animation: pulse 1s ease-in-out infinite;
  background: ${p => p.theme.colors.gray400};
  border-radius: 50%;
  height: 6px;
  width: 6px;

  @keyframes pulse {
    0%,
    80%,
    100% {
      opacity: 0.3;
    }
    40% {
      opacity: 1;
    }
  }
`;
